import * as React from 'react';

import { classNames, useConfirm, useTranslation, useSearchFilters, Tag, useSearch } from '@components';
import { IOption, ISecurity, IWork, ModuleManager } from '@models';
import {
    messageTypes, IMessage, MessageType, IMessageWithSearch
} from '@models/notifications';
import { NotificationMessageEffect } from './NotificationMessageEffect';
import NotificationMessage from './NotificationMessage';
import * as DateUtils from '@utils/date-utils';

import './NotificationsCenter.scss';

type ResolveMessageF = (id: string) => Promise<IOption<boolean>>;
type ResolveMessagesF = (ids: string[]) => Promise<IOption<boolean>>;

export interface IProps {
    className?: string;
    messages: IMessage[];
    loadMessages: Function;
    resolveMessage: ResolveMessageF;
    resolveMessages: ResolveMessagesF;
    security: ISecurity;
    work: IWork;
    workId: number;
    moduleManager: ModuleManager;
}



const filterMessage = (message: IMessageWithSearch, filters: any) => {
    return (filters.messageType == undefined || message.notificationType == filters.messageType)
        && (filters.job == null || filters.job == '' || message.jobSearch.includes(filters.job.toLowerCase()))
        && (filters.contractor == null || filters.contractor == '' || message.contractorSearch.includes(filters.contractor.toLowerCase()))
        && (filters.message == null || filters.message == '' || message.messageSearch.includes(filters.message.toLowerCase()))
        && (message.message != null)
        && (message.message != '')
        && (message.notificationType == MessageType.RELOAD_NOTIFICATIONS);
}

const filterMessages = (messages: IMessageWithSearch[], filters: any) => {
    return messages.filter(m => filterMessage(m, filters));
}

const contractorProperties = ['ContractorName', 'ContractorCode'];
const jobProperties = ['JobCode', 'JobName', 'JobDescription'];

const parseMessageProps = (data: any, propNames: string[]) => {
    const props = propNames.map(p => data[p] ?? '').join('').toLowerCase();
    return props ?? '';
}

const parseMessages = (messages: IMessage[]) => {
    return messages.map(m => ({
        ...m,
        jobSearch: parseMessageProps(m.data ?? {}, jobProperties),
        contractorSearch: parseMessageProps(m.data ?? {}, contractorProperties),
        messageSearch: m.message?.toLowerCase(),
    }));
}

const PAGE_SIZE = 50;
const DEFAULT_SORT_ASC = false;

const toTime = (date?: Date | string) => {
    if (date == null || date === '') {
        return 0;
    }

    const d = DateUtils.parseDate(date);
    return (d instanceof Date && !Number.isNaN(d.getTime())) ? d.getTime() : 0;
}

const excludeNotificationTypes = [
    MessageType.INVALID_ITA,
    MessageType.RESOURCE_AUTHORIZED,
    MessageType.RESOURCE_REJECTED,
    // MessageType.REQUIREMENT_EXPIRED,
];


export function NotificationsCenter(props: IProps) {
    const { t } = useTranslation();
    const timeout = React.useRef<NodeJS.Timeout>();

    const originalMessages = React.useRef<IMessageWithSearch[]>([]);

    const confirm = useConfirm({
        message: t('notifications.messages.close-confirm'),
        accept: () => doRemoveCheckedMessages(),
    });

    React.useEffect(() => {
         init().then(() => {
            if (props.workId != null) {
                setMessages(originalMessages.current
                    .filter(om => om.data.WorkId == props.workId));
            } else {
                setMessages(originalMessages.current);
            }
        });
    }, []);

    async function init() {
        // const moduleMessages = await Promise.resolve(props.moduleManager.getMessages(props));
        const moduleMessagesPromise = props.moduleManager.getMessages(props);
        const moduleMessages = await Promise.resolve(moduleMessagesPromise);
        if (moduleMessages && moduleMessages.length > 0) {
            originalMessages.current = parseMessages(moduleMessages);
        } else {
            originalMessages.current = parseMessages(props.messages);
        }
    }

    const [messages, setMessages] = React.useState<IMessageWithSearch[]>(originalMessages.current);
    const [selectedMessage, setSelectedMessage] = React.useState<IMessage | undefined>();
    const [checkedIds, setCheckedIds] = React.useState<ReadonlySet<string>>(() => new Set<string>());
    const [sortAsc, setSortAsc] = React.useState(DEFAULT_SORT_ASC);


    const filterMessageTypes = (messageTypes : { id: MessageType; name: string;}[] ): { id: MessageType; name: string; }[] => {

        const result = messageTypes;

        switch (props.workId) {
            case 21:
                return result.filter(m => !excludeNotificationTypes.includes(m.id));
            case 23:
                return result.filter(m => m.id !== MessageType.MESSAGE);
            default:
                return result;
        }
    }

    const filters = useSearchFilters({
        name: 'notifications/list',
        workId: props.workId ?? 0,
        references: {
            messageType: filterMessageTypes(messageTypes)
        },
        persist: true
    });

    const notificationsSearch = useSearch({
        workId: props.workId,
        search: props.security.isGestor() ? 'notifications/gestorNotifications' : 'notifications/userNotifications',
        lazy: true,
        filters : {
            ...filters.filters,
            workId: props.workId,
            userId: props.security.user?.id ?? 0
        }
    });

    React.useEffect(() => {
        if(filters.hasFilters()){
            if(filters.filters.messageType == 0){
                filters.filters.messageType = undefined;
            }
            notificationsSearch.doSearch();
        }
    }, [filters.filters]);

    React.useEffect(() => {
        if (notificationsSearch.value && filters.hasFilters()) {
            const searchValue : IMessage[] = notificationsSearch.value as IMessage[];
            setMessages(parseMessages(searchValue));
        }else{
            if (props.workId != null) {
                setMessages(originalMessages.current
                    .filter(om => om.data.WorkId == props.workId));
                    } else {
                        setMessages(originalMessages.current);
                    }
        }
    }, [notificationsSearch.value]);

    const loadMessages = () => {
        props.loadMessages();

        const time = (Math.random() * 120 + 30) * 1000;

        timeout.current = setTimeout(loadMessages, time);
    }

    React.useEffect(() => {
        // props.loadMessages();

        timeout.current = setTimeout(() => {
            loadMessages();
        }, 2000);

        return () => {
            if (timeout.current) {
                clearTimeout(timeout.current);
                timeout.current = undefined;
            }
        }
    }, []);

    React.useEffect(() => {
        init().then(() => {
            if (props.workId != null) {
                setMessages(originalMessages.current
                    .filter(om => om.data.WorkId == props.workId));
            } else {
                setMessages(originalMessages.current);
            }
        });
    }, [props.messages]);

    const messagesToRender = React.useMemo(
        () => {
            const filtered = messages.filter(m => m.notificationType != MessageType.RELOAD_NOTIFICATIONS);
            const direction = sortAsc ? 1 : -1;
            return [...filtered].sort((a, b) => (toTime(a.date) - toTime(b.date)) * direction);
        },
        [messages, sortAsc]);

    const doRemoveCheckedMessages = () => {
        props.resolveMessages(Array.from(checkedIds));
    }

    const doCheckAllMessages = () => {
        if (checkedIds.size == messagesToRender.length) {
            setCheckedIds(new Set<string>());
        }
        else {
            setCheckedIds(new Set(messagesToRender.map(m => m.id)));
        }
    }

    const toggleCheck = (id: string) => {
        setCheckedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            }
            else {
                next.add(id);
            }
            return next;
        });
    }

    const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);

    React.useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [messagesToRender]);

    const loadMoreRef = React.useRef<HTMLDivElement>(null);
    const scrollRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const target = loadMoreRef.current;
        if (!target || typeof IntersectionObserver == 'undefined') {
            return;
        }

        const observer = new IntersectionObserver(entries => {
            if (entries.some(e => e.isIntersecting)) {
                setVisibleCount(c => Math.min(c + PAGE_SIZE, messagesToRender.length));
            }
        }, { root: scrollRef.current ?? null, rootMargin: '200px' });

        observer.observe(target);
        return () => observer.disconnect();
    }, [messagesToRender.length]);

    const visibleMessages = messagesToRender.slice(0, visibleCount);

    return <div className={classNames(props.className, 'notifications-center')}>
        <div className='notifications-title'>
            {t('Notifications')}
            <Tag
                className='small sm mr-left'
                value={messagesToRender.length} />
            <img src="/img/works/notifications.png" alt="gstore" />
            <button
                type='button'
                className='notifications-sort'
                onClick={() => setSortAsc(s => !s)}
                title={sortAsc ? t('notifications.sort.asc') : t('notifications.sort.desc')}>
                <i className={sortAsc ? "fas fa-sort-up" : "fas fa-sort-down"} />
                <span className='notifications-sort-label'>
                    {t('notifications.sort.by')} {sortAsc ? t('notifications.sort.asc') : t('notifications.sort.desc')}
                </span>
            </button>
            <span className='e' />
            <div className='search-filters' style={{ padding: "10px" }}>
                <i onClick={doCheckAllMessages} className="fas fa-check-double" title={t('notifications.remove.all')} style={{ color: "#717171" }} />
            </div>
            <div className='search-filters' style={{ padding: "10px" }}>
                <i className='fas fa-trash' onClick={checkedIds.size == 0 ? undefined : confirm} title={t('notifications.remove')} style={{ color: "#717171" }} />
            </div>
            {filters.renderAsButton()}
        </div>

        <div className='notifications' ref={scrollRef}>
            {selectedMessage &&
                <NotificationMessageEffect
                    message={selectedMessage}
                    requestCancel={() => setSelectedMessage(undefined)}
                    resolveMessage={props.resolveMessage}
                    security={props.security}
                    workId={props.workId}
                    work={props.work} />}
            {visibleMessages.map(m =>
                <NotificationMessage
                    key={m.id}
                    message={m}
                    resolveMessage={props.resolveMessage}
                    onSelect={setSelectedMessage}
                    checked={checkedIds.has(m.id)}
                    onCheck={() => toggleCheck(m.id)}
                />)}
            {visibleCount < messagesToRender.length &&
                <div className='notifications-more' ref={loadMoreRef}
                    onClick={() => setVisibleCount(c => Math.min(c + PAGE_SIZE, messagesToRender.length))}>
                    {t('notifications.load-more')} ({messagesToRender.length - visibleCount})
                </div>}
        </div>
    </div>
}
